import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { WalletProvider, useWallet } from "@/context/WalletContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import ErrorBoundary from "@/components/ErrorBoundary";

// Log app boot
console.log('[APP] App.js loading...');
if (typeof window !== 'undefined' && window.__bootLog) {
  window.__bootLog.push('[APP] App.js module loaded');
}

// Pages
import Landing from "@/pages/Landing";
import News from "@/pages/News";
import NewsArticle from "@/pages/NewsArticle";
import Dashboard from "@/pages/Dashboard";
import AgentCreate from "@/pages/AgentCreate";
import AgentDetail from "@/pages/AgentDetail";
import Community from "@/pages/Community";
import Profile from "@/pages/Profile";
import Explore from "@/pages/Explore";
import Leaderboard from "@/pages/Leaderboard";
import WhaleLeaderboard from "@/pages/WhaleLeaderboard";
import AgentDirectory from "@/pages/AgentDirectory";
import AdminWithdrawals from "@/pages/AdminWithdrawals";
import ArbitrageBotCreate from "@/pages/ArbitrageBotCreate";
import ArbitrageBotDashboard from "@/pages/ArbitrageBotDashboard";
import BotShare from "@/pages/BotShare";
import ActivityShare from "@/pages/ActivityShare";
import PostShare from "@/pages/PostShare";
import Perps from "@/pages/Perps";
import AdminPerps from "@/pages/AdminPerps";
import AdminBots from "@/pages/AdminBots";
import AdminFlip from "@/pages/AdminFlip";
import AdminWallets from "@/pages/AdminWallets";
import Pulse from "@/pages/Pulse";
import AdminOrphans from "@/pages/AdminOrphans";
import AdminBondTracker from "@/pages/AdminBondTracker";
import AdminSupport from "@/pages/AdminSupport";
import Support from "@/pages/Support";
import BotsList from "@/pages/BotsList";
import FlipMarket from "@/pages/FlipMarket";
import LPFlipsMarket from "@/pages/LPFlipsMarket";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// API Service with auth
export const createApiService = (getAuthHeaders) => ({
  // Auth
  connectWallet: (data) => axios.post(`${API}/auth/connect`, data),
  getMe: () => axios.get(`${API}/auth/me`, { headers: getAuthHeaders() }),
  updateMe: (data) => axios.patch(`${API}/auth/me`, data, { headers: getAuthHeaders() }),
  
  // Users
  getUser: (id) => axios.get(`${API}/users/${id}`),
  getUserByAddress: (address) => axios.get(`${API}/users/address/${address}`),
  followUser: (id) => axios.post(`${API}/users/${id}/follow`, {}, { headers: getAuthHeaders() }),
  unfollowUser: (id) => axios.delete(`${API}/users/${id}/follow`, { headers: getAuthHeaders() }),
  getFollowers: (id) => axios.get(`${API}/users/${id}/followers`),
  getFollowing: (id) => axios.get(`${API}/users/${id}/following`),
  getUserPosts: (id) => axios.get(`${API}/users/${id}/posts`),
  
  // Agents - longer timeout for agent creation due to balance fetching
  createAgent: (data) => axios.post(`${API}/agents`, data, { 
    headers: getAuthHeaders(),
    timeout: 30000  // 30 second timeout for agent creation
  }),
  getAgents: () => axios.get(`${API}/agents`, { headers: getAuthHeaders() }),
  exploreAgents: (sortBy = 'likes_count') => axios.get(`${API}/agents/explore?sort_by=${sortBy}`),
  getAgent: (id) => axios.get(`${API}/agents/${id}`),
  deleteAgent: (id) => axios.delete(`${API}/agents/${id}`, { headers: getAuthHeaders() }),
  cloneAgent: (id) => axios.post(`${API}/agents/${id}/clone`, {}, { headers: getAuthHeaders() }),
  likeAgent: (id) => axios.post(`${API}/agents/${id}/like`, {}, { headers: getAuthHeaders() }),
  unlikeAgent: (id) => axios.delete(`${API}/agents/${id}/like`, { headers: getAuthHeaders() }),
  
  // Portfolio & Activities
  getPortfolio: (id) => axios.get(`${API}/agents/${id}/portfolio`),
  getActivities: (id, limit = 50) => axios.get(`${API}/agents/${id}/activities?limit=${limit}`),
  getRecentActivities: (limit = 20) => axios.get(`${API}/activities/recent?limit=${limit}`),
  
  // Market Data
  getPegStatus: () => axios.get(`${API}/market/peg-status`),
  getYieldStrategies: () => axios.get(`${API}/market/yield-strategies`),
  
  // AI & Simulation
  requestDecision: (id, context) => axios.post(`${API}/agents/${id}/decide`, { agent_id: id, market_context: context }, { headers: getAuthHeaders() }),
  simulateCycle: (id) => axios.post(`${API}/agents/${id}/simulate-cycle`, {}, { headers: getAuthHeaders() }),
  refreshBalances: (id) => axios.post(`${API}/agents/${id}/refresh-balances`, {}, { headers: getAuthHeaders() }),
  
  // Posts/Social
  createPost: (data) => axios.post(`${API}/posts`, data, { headers: getAuthHeaders() }),
  getFeed: () => axios.get(`${API}/posts/feed`, { headers: getAuthHeaders() }),
  getTrending: () => axios.get(`${API}/posts/trending`),
  getPost: (id) => axios.get(`${API}/posts/${id}`),
  deletePost: (id) => axios.delete(`${API}/posts/${id}`, { headers: getAuthHeaders() }),
  likePost: (id) => axios.post(`${API}/posts/${id}/like`, {}, { headers: getAuthHeaders() }),
  unlikePost: (id) => axios.delete(`${API}/posts/${id}/like`, { headers: getAuthHeaders() }),
  
  // Leaderboard
  getLeaderboard: (limit = 50, timeframe = 'all') => axios.get(`${API}/leaderboard?limit=${limit}&timeframe=${timeframe}`),
  
  // Stats
  getStats: () => axios.get(`${API}/stats`),
  
  // Prediction Bots (Arbitrage Hunter)
  createBot: (data) => axios.post(`${API}/prediction-bots`, data, { headers: getAuthHeaders() }),
  getBots: () => axios.get(`${API}/prediction-bots`, { headers: getAuthHeaders() }),
  getBot: (id) => axios.get(`${API}/prediction-bots/${id}`, { headers: getAuthHeaders() }),
  startBot: (id) => axios.post(`${API}/prediction-bots/${id}/start`, {}, { headers: getAuthHeaders() }),
  stopBot: (id) => axios.post(`${API}/prediction-bots/${id}/stop`, {}, { headers: getAuthHeaders() }),
  updateBot: (id, data) => axios.patch(`${API}/prediction-bots/${id}`, data, { headers: getAuthHeaders() }),
  deleteBot: (id) => axios.delete(`${API}/prediction-bots/${id}`, { headers: getAuthHeaders() }),
  depositToBot: (id, amount) => axios.post(`${API}/prediction-bots/${id}/deposit?amount=${amount}`, {}, { headers: getAuthHeaders() }),
  withdrawFromBot: (id, amount) => axios.post(`${API}/prediction-bots/${id}/withdraw?amount=${amount}`, {}, { headers: getAuthHeaders() }),
  getBotPositions: (id) => axios.get(`${API}/prediction-bots/${id}/positions`, { headers: getAuthHeaders() }),
  getBotTrades: (id, limit = 50) => axios.get(`${API}/prediction-bots/${id}/trades?limit=${limit}`, { headers: getAuthHeaders() }),
  getBotStrategies: () => axios.get(`${API}/prediction-bots/strategies`),
  
  // Generic methods for direct API access
  get: (url) => axios.get(`${API}${url}`, { headers: getAuthHeaders() }),
  post: (url, data = {}) => axios.post(`${API}${url}`, data, { headers: getAuthHeaders() }),
  put: (url, data = {}) => axios.put(`${API}${url}`, data, { headers: getAuthHeaders() }),
  patch: (url, data = {}) => axios.patch(`${API}${url}`, data, { headers: getAuthHeaders() }),
  delete: (url) => axios.delete(`${API}${url}`, { headers: getAuthHeaders() }),
});

// Default API service (for unauthenticated calls)
export const apiService = createApiService(() => ({}));

function AppRoutes() {
  const location = useLocation();

  // Reset scroll to top on every route change. React Router v6 preserves the
  // window scroll position across client-side navigations, which made pages
  // like /dashboard open partway scrolled down (whatever Y the previous page
  // was at). Force manual restoration + scroll to top on each pathname change.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Track page views
  useEffect(() => {
    const trackPageView = async () => {
      try {
        await fetch(`${API}/track/pageview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: location.pathname,
            referrer: document.referrer || null
          })
        });
      } catch (e) {
        // Silently fail - analytics shouldn't break the app
        console.debug('[Analytics] Page view tracking failed:', e);
      }
    };
    
    trackPageView();
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/bot/share/:botId" element={<BotShare />} />
      <Route path="/activity/:activityId" element={<ActivityShare />} />
      <Route path="/post/:postId" element={<PostShare />} />
      <Route path="/news" element={<News />} />
      <Route path="/news/category/:category" element={<News />} />
      <Route path="/news/:slug" element={<NewsArticle />} />
      <Route path="/perps" element={<Perps />} />
      <Route path="/admin/perps" element={<AdminPerps />} />
      <Route path="/admin/bots" element={<AdminBots />} />
      <Route path="/admin/flip" element={<AdminFlip />} />
      <Route path="/admin/wallets" element={<AdminWallets />} />
      <Route path="/pulse" element={<Pulse />} />
      <Route path="/admin/orphans" element={<AdminOrphans />} />
      <Route path="/admin/bond-tracker" element={<AdminBondTracker />} />
      <Route path="/admin/support" element={<AdminSupport />} />
      <Route path="/support" element={<Support />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/create" element={<AgentCreate />} />
      <Route path="/launch" element={<AgentCreate />} />
      <Route path="/agent/:id" element={<AgentDetail />} />
      <Route path="/community" element={<Community />} />
      <Route path="/profile/:id" element={<Profile />} />
      <Route path="/explore" element={<Explore />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/whale-leaderboard" element={<WhaleLeaderboard />} />
      <Route path="/agent-directory" element={<AgentDirectory />} />
      <Route path="/admin/withdrawals" element={<AdminWithdrawals />} />
      <Route path="/bot/create" element={<ArbitrageBotCreate />} />
      <Route path="/bot/:botId" element={<ArbitrageBotDashboard />} />
      <Route path="/bots" element={<BotsList />} />
      <Route path="/flip-market" element={<FlipMarket />} />
      <Route path="/lp-flips" element={<LPFlipsMarket />} />
      {/* Catch-all: prevent stale/typo links from rendering as a blank page */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center gap-4">
            <h1 className="font-heading text-4xl font-bold tracking-wider">404</h1>
            <p className="text-muted-foreground max-w-md">
              The page you&apos;re looking for doesn&apos;t exist or has moved.
            </p>
            <button
              onClick={() => window.location.assign('/')}
              className="mt-2 px-6 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="notfound-go-home"
            >
              Return home
            </button>
          </div>
        }
      />
    </Routes>
  );
}

function App() {
  // Log when App component renders
  useEffect(() => {
    console.log('[APP] App component mounted');
    if (typeof window !== 'undefined') {
      if (window.__bootLog) {
        window.__bootLog.push('[APP] App component mounted');
      }
      // Hide loading indicator
      const loader = document.getElementById('loading-indicator');
      if (loader) {
        loader.style.display = 'none';
      }
    }
  }, []);

  return (
    <ErrorBoundary>
      <WalletProvider>
        <CurrencyProvider>
          <div className="App min-h-screen bg-background">
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
            <Toaster 
            position="bottom-right" 
            toastOptions={{
              style: {
                background: 'rgba(10, 10, 10, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#EDEDED',
              },
            }}
          />
          </div>
        </CurrencyProvider>
      </WalletProvider>
    </ErrorBoundary>
  );
}

export default App;
